import { Fragment, useEffect } from 'react';
import { Icon, Link, Logo, PanelBar, Page, Wave, ui } from '../ui';
import { BUSINESS, BUSINESS_FACTS, LEGAL_UPDATED } from '../legal';

interface Clause {
  heading: string;
  body: React.ReactNode;
}

const EMAIL_HREF = `mailto:${BUSINESS.email}`;
const COMMUNITY_HREF = 'https://community.goodbuild.cc';

const DOCUMENTS = [
  { href: '/legal', title: 'Legal', blurb: 'Who runs this site, and how to reach them.' },
  {
    href: '/legal/terms',
    title: 'Terms',
    blurb: 'What this site is, and what you may do with it.',
  },
  {
    href: '/legal/privacy',
    title: 'Privacy',
    blurb: 'What this site knows about you, which is nothing.',
  },
];

function useTabTitle(title: string): void {
  useEffect(() => {
    document.title = `${title} — GOOD BUILD`;
    return () => {
      document.title = 'GOOD BUILD';
    };
  }, [title]);
}

function Switch({ current }: { current: string }): React.ReactElement {
  return (
    <nav className={ui.legalSwitch ?? ''} aria-label="Legal documents">
      {DOCUMENTS.map((entry) => (
        <Link
          key={entry.href}
          href={entry.href}
          className={
            entry.href === current
              ? `${ui.legalTab ?? ''} ${ui.legalTabCurrent ?? ''}`
              : (ui.legalTab ?? '')
          }
          {...(entry.href === current ? { 'aria-current': 'page' as const } : {})}
        >
          {entry.title}
        </Link>
      ))}
    </nav>
  );
}

function Imprint(): React.ReactElement {
  return (
    <section className={ui.legalImprint ?? ''}>
      <h2 className="gb-subhead">Who runs this site</h2>
      <dl className={ui.details ?? ''}>
        {BUSINESS_FACTS.map((fact) => (
          <Fragment key={fact.label}>
            <dt className={ui.detailLabel ?? ''}>{fact.label}</dt>
            <dd className={ui.detailValue ?? ''}>{fact.value}</dd>
          </Fragment>
        ))}
        <dt className={ui.detailLabel ?? ''}>Email</dt>
        <dd className={ui.detailValue ?? ''}>
          <Link href={EMAIL_HREF}>{BUSINESS.email}</Link>
        </dd>
      </dl>
    </section>
  );
}

function LegalDocument({
  current,
  title,
  lede,
  clauses = [],
  children,
}: {
  current: string;
  title: string;
  lede: string;
  clauses?: readonly Clause[];
  children?: React.ReactNode;
}): React.ReactElement {
  useTabTitle(title);

  return (
    <Page
      mark={false}
      bleed={
        <div className={ui.legalMastheadWrap ?? ''}>
          <header className={ui.legalMasthead ?? ''}>
            <div className={ui.legalMastheadInner ?? ''}>
              <Logo horizontal size="1rem" />
              <h1 className="gb-headline">{title}</h1>
              <p className="gb-lead">{lede}</p>
            </div>
          </header>
          <Wave className={ui.legalWave ?? ''} />
        </div>
      }
    >
      <div className={ui.legal ?? ''}>
        <Switch current={current} />

        {children}

        {clauses.map((clause, index) => (
          <section key={clause.heading} className={ui.legalClause ?? ''}>
            <p className={ui.legalClauseNumber ?? ''}>{String(index + 1).padStart(2, '0')}</p>
            <h2 className="gb-subhead">{clause.heading}</h2>
            {clause.body}
          </section>
        ))}

        <Imprint />

        <footer className={ui.legalFoot ?? ''}>
          <PanelBar />
          <p className={ui.legalChanged ?? ''}>Last changed {LEGAL_UPDATED}</p>
        </footer>
      </div>
    </Page>
  );
}

export function LegalRoute(): React.ReactElement {
  return (
    <LegalDocument
      current="/legal"
      title="Legal"
      lede="Who runs this site, what you may do with it, and what it knows about you."
    >
      <div className={ui.legalCards ?? ''}>
        {DOCUMENTS.filter((entry) => entry.href !== '/legal').map((entry) => (
          <Link key={entry.href} href={entry.href} className={ui.card ?? ''}>
            <h2 className={ui.cardTitle ?? ''}>{entry.title}</h2>
            <p className={ui.cardMeta ?? ''}>{entry.blurb}</p>
            <span className={ui.cardGo ?? ''}>
              Read it
              <Icon name="arrow-right" size={16} />
            </span>
          </Link>
        ))}
      </div>
    </LegalDocument>
  );
}

const TERMS: readonly Clause[] = [
  {
    heading: 'What this site is',
    body: (
      <p className="gb-body">
        goodbuild.cc is our shop window. You can look at the games we make, read what each one is,
        and follow a link to a shop that sells it. There is nothing to sign up for and nothing to
        buy here.
      </p>
    ),
  },
  {
    heading: 'What you may do with it',
    body: (
      <p className="gb-body">
        Look at it, link to it, and take screenshots. If you make videos or write about games, use
        anything you find here and you do not need to ask us first. What you may not do is take the
        games, the art, the music, the names or the site itself and put your own name on them.
      </p>
    ),
  },
  {
    heading: 'Where the buttons go',
    body: (
      <p className="gb-body">
        A button on a game case takes you to Steam, Epic or another shop. Those shops are not ours.
        What you pay, what you get and who to ask for a refund are all theirs, under their own
        terms.
      </p>
    ),
  },
  {
    heading: 'Our forum is a different site',
    body: (
      <p className="gb-body">
        GOOD BUILD COMMUNITY at <Link href={COMMUNITY_HREF}>community.goodbuild.cc</Link> is ours
        too, but it is a separate site with a sign-in and rules of its own. Nothing on this page
        covers it.
      </p>
    ),
  },
  {
    heading: 'What we cannot promise',
    body: (
      <p className="gb-body">
        We keep the site up and we fix what breaks, but we cannot promise it is never down and never
        wrong. A release date on a case is a plan rather than a promise. Plans move, and when one
        moves we change the case.
      </p>
    ),
  },
  {
    heading: 'If something is wrong',
    body: (
      <p className="gb-body">
        Write to <Link href={EMAIL_HREF}>{BUSINESS.email}</Link> and we will sort it out. Polish law
        applies to this site. If you live somewhere else in Europe, the protection your own country
        gives you still stands — nothing here takes it away.
      </p>
    ),
  },
  {
    heading: 'If this page changes',
    body: <p className="gb-body">We change the date at the bottom of it.</p>,
  },
];

export function TermsRoute(): React.ReactElement {
  return (
    <LegalDocument
      current="/legal/terms"
      title="Terms"
      lede="This site shows the games we make. That is all it does, so this is short."
      clauses={TERMS}
    />
  );
}

const PRIVACY: readonly Clause[] = [
  {
    heading: 'There is nothing to collect',
    body: (
      <p className="gb-body">
        There is no sign-in on this site, no form, no basket and no comment box. You cannot tell us
        anything, so we do not learn anything. We run no analytics, we set no cookies, and we do not
        follow you here or anywhere else.
      </p>
    ),
  },
  {
    heading: 'What your browser keeps for you',
    body: (
      <p className="gb-body">
        Two small things are saved on your own device and never sent anywhere: the order of the pile
        of cases, so the one you looked at last is on top next time, and whether you turned the
        sound on. Both sit in your browser's own storage. Clearing your browsing data clears them.
      </p>
    ),
  },
  {
    heading: 'Who your browser talks to',
    body: (
      <>
        <p className="gb-body">
          Two companies see a request from your browser when this page loads, because the page comes
          from them.
        </p>
        <ul className={ui.legalList ?? ''}>
          <li className="gb-body">
            <strong>Cloudflare</strong> serves the site. Like every host it sees the request your
            browser makes, including your IP address, so it can send the page back. It gives us
            counts — how many people came, and roughly from where — and a count is not a person.
          </li>
          <li className="gb-body">
            <strong>Google Fonts</strong> serves the three typefaces the site is set in. Your
            browser fetches them from Google, so Google sees your IP address at that moment. Nothing
            else about you goes with it.
          </li>
        </ul>
      </>
    ),
  },
  {
    heading: 'If you write to us',
    body: (
      <p className="gb-body">
        An email is an email. If you send one we have it, and it sits in our mailbox — which Google
        runs for us — until we delete it. Ask us to delete it and we will.
      </p>
    ),
  },
  {
    heading: 'Where the buttons go',
    body: (
      <p className="gb-body">
        A button on a game case takes you to Steam, Epic or another shop. From the moment you arrive
        you are on their site under their privacy policy, not ours.
      </p>
    ),
  },
  {
    heading: 'Your rights',
    body: (
      <p className="gb-body">
        The law gives you the right to see what a company holds about you, to correct it and to have
        it deleted. We hold nothing, so there is nothing to show you — unless you have emailed us,
        and then it is that email. Ask at <Link href={EMAIL_HREF}>{BUSINESS.email}</Link>. The
        business answerable for all of this is the one at the foot of this page. If you think we
        have got it wrong you can complain to UODO, the Polish data protection authority, at{' '}
        <Link href="https://uodo.gov.pl">uodo.gov.pl</Link>.
      </p>
    ),
  },
  {
    heading: 'If this page changes',
    body: <p className="gb-body">We change the date at the bottom of it.</p>,
  },
];

export function PrivacyRoute(): React.ReactElement {
  return (
    <LegalDocument
      current="/legal/privacy"
      title="Privacy"
      lede="This site collects nothing about you. No accounts, no cookies, no analytics, no tracking."
      clauses={PRIVACY}
    />
  );
}
